import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { incaAccessor } from '@cancercentrum/inca';
import { Badge, Button, Card, Dropdown, ExpandableText, MenuItem, OverlayTrigger, Tooltip } from '@cancercentrum/rcc-bootstrap';
import { useCallback, useMemo, useState } from 'react';
import orderBy from 'lodash/orderBy';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import groupBy from 'lodash/groupBy';
import invariant from 'invariant';
import { faBug, faComment, faLayerGroup, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { library } from '@fortawesome/fontawesome-svg-core';
import { useRowModel } from '../../hooks/useRowModel';
import usePromise from '../../hooks/usePromise';
import { AjaxErrorAlert } from '../AjaxErrorAlert';
import { excludedErrandButtonRules as excludedRulesSuperConnection } from '../unsafe_Connection/excludedErrandButtonRules';
import { useErrandPanelApi } from './ErrandPanelApi';
import { Buttons, reduceButtonRules } from './SimplifiedErrandType/Buttons';
import { useErrandPanelContext } from './ErrandPanel';
import { getCustomButtons } from './SimplifiedErrandType/getCustomButtons';
import { errandButtonUtils } from './errandButtonUtils';
library.add(faBug, faTrashAlt); // ikoner som används i customknappar måste registreras globalt om inte hela fontawesome länkas in
export const ErrandPanelComponent = (props)=>{
    const { buttons = Buttons } = props;
    const ctx = useErrandPanelContext();
    const dfApi = useErrandPanelApi();
    invariant(dfApi, 'ErrandPanel requires ErrandPanelApiContext to be created in a parent component');
    const inca = incaAccessor();
    const row = useRowModel();
    const customButtons = useMemo(()=>getCustomButtons(inca), [
        inca
    ]);
    const superConnectionEnabled = inca.form.designedForm.useRegisterRecordConnections;
    const buttonsWithState = orderBy([
        ...buttons,
        ...customButtons
    ].map((btn)=>({
            button: btn,
            state: dfApi.events.onEvaluateButtonState.trigger(()=>[
                    btn.key,
                    row,
                    inca,
                    ctx
                ], reduceButtonRules(superConnectionEnabled && btn.superConnectionRules ? [
                ...btn.defaultRules.filter((rule)=>!excludedRulesSuperConnection.includes(rule.id)),
                ...btn.superConnectionRules
            ] : btn.defaultRules, ctx))
        })), (x)=>x.button.prio);
    const visibleButtons = buttonsWithState.filter((x)=>x.state.state !== 'Hidden');
    const buttonGroups = useMemo(()=>groupBy(visibleButtons, (x)=>x.button.section), [
        visibleButtons
    ]);
    const [modalInfo, setModalInfo] = useState(null);
    const p = usePromise(async ()=>{}, {
        executingButton: undefined
    });
    const triggerOnClick = useCallback((btn, r, i)=>{
        return btn.onClick(r, i, ()=>new Promise((resolve)=>{
                setModalInfo({
                    button: btn,
                    resolve: (x)=>{
                        setModalInfo(null);
                        resolve(x);
                    }
                });
            }), ctx, dfApi);
    }, [
        ctx,
        dfApi
    ]);
    /**
     * Returns errandButtons as elements inside a
     * DropdownButton if the viewport is narrower than
     * the Bs 'xl' breakpoint, otherwise it returns
     * the buttons like Button elements.
     * @see {@link getButtonElements}
     * @param errandButtons The buttons to return as elements
     */ const getButtonElementsDropdownWrapper = (errandButtons)=>{
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsxs(Dropdown, {
                    className: "d-xl-none",
                    children: [
                        /*#__PURE__*/ _jsx(Dropdown.Toggle, {
                            bsStyle: "default-1",
                            icon: faLayerGroup,
                            children: "Verktyg"
                        }),
                        /*#__PURE__*/ _jsx(Dropdown.Menu, {
                            role: "custom-buttons-menu",
                            children: errandButtons.map((btn)=>{
                                return /*#__PURE__*/ _jsxs(MenuItem, {
                                    className: "mt-1",
                                    disabled: p.isLoading || btn.state.state === 'Disabled',
                                    onClick: ()=>p.exec(triggerOnClick(btn.button, row, inca), {
                                            executingButton: btn.button.key
                                        }),
                                    children: [
                                        /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                                            icon: btn.button.icon,
                                            className: "me-2 text-primary"
                                        }),
                                        btn.button.text
                                    ]
                                }, btn.button.key);
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "d-none d-xl-block",
                    children: getButtonElements(errandButtons)
                })
            ]
        });
    };
    /* TODO
         - visa incaknappar (importera data)
    */ const getButtonElements = (errandButtons)=>{
        return errandButtons.map((btn)=>{
            const ButtonEl = /*#__PURE__*/ _jsx(Button, {
                icon: btn.button.icon,
                onClick: ()=>p.exec(triggerOnClick(btn.button, row, inca), {
                        executingButton: btn.button.key
                    }),
                disabled: p.isLoading || btn.state.state === 'Disabled',
                title: btn.state.tooltip,
                bsStyle: btn.button.buttonStyle,
                className: "me-1 mt-1 mt-md-0",
                loading: p.isLoading && p.meta?.executingButton === btn.button.key,
                children: btn.button.text
            }, btn.button.key);
            if (btn.state.tooltip) {
                return /*#__PURE__*/ _jsx(OverlayTrigger, {
                    overlay: /*#__PURE__*/ _jsx(Tooltip, {
                        id: `Popover_${btn.button.key}`,
                        className: "mt-2",
                        children: btn.state.tooltip
                    }),
                    trigger: [
                        'hover',
                        'focus'
                    ],
                    placement: "bottom",
                    children: /*#__PURE__*/ _jsx("div", {
                        children: ButtonEl
                    })
                }, btn.button.key);
            }
            return ButtonEl;
        });
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "errand-panel",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "d-flex mb-2",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "d-flex align-items-center",
                    style: {
                        overflow: 'hidden'
                    },
                    children: [
                        ctx.errand.priority === 'Brådskande' && /*#__PURE__*/ _jsx(Badge, {
                            bsStyle: "warning",
                            className: "me-1 text-dark",
                            title: "Ärendet är markerat som brådskande.",
                            children: "Brådskande"
                        }),
                        errandButtonUtils.getExistingTags(ctx).map((x)=>/*#__PURE__*/ _jsx(Badge, {
                                bsStyle: "secondary",
                                className: "me-1",
                                children: x
                            }, x))
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("h1", {
                className: "me-2",
                style: {
                    whiteSpace: 'nowrap'
                },
                children: [
                    ctx.errand.document.registerName,
                    " - ",
                    ctx.errand.document.formName
                ]
            }),
            /*#__PURE__*/ _jsx(Card, {
                className: "d-print-none errand-panel-card",
                children: /*#__PURE__*/ _jsxs(Card.Body, {
                    className: "errand-panel-card-body",
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "d-flex justify-content-between flex-wrap",
                            children: [
                                'start',
                                'middle',
                                'end'
                            ].map((group)=>{
                                return buttonGroups[group]?.length && (group === 'middle' ? /*#__PURE__*/ _jsx("div", {
                                    className: "d-flex py-1",
                                    children: getButtonElementsDropdownWrapper(buttonGroups[group])
                                }, group) : /*#__PURE__*/ _jsx("div", {
                                    className: "d-flex py-1 flex-wrap",
                                    children: getButtonElements(buttonGroups[group])
                                }, group));
                            })
                        }),
                        p.error && /*#__PURE__*/ _jsx(AjaxErrorAlert, {
                            error: p.error,
                            className: "mt-2"
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                title: ctx.errand.comment ?? 'Empty comment',
                className: "d-flex overflow-hidden pb-4 d-print-none errand-panel-comment",
                children: [
                    /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                        icon: faComment,
                        className: `${ctx.errand.comment ? 'text-warning' : 'text-secondary'} me-2 mt-1`
                    }),
                    /*#__PURE__*/ _jsx(ExpandableText, {
                        text: ctx.errand.comment ?? '',
                        btnClassName: "p-0"
                    })
                ]
            }),
            modalInfo?.button?.ModalComponent && /*#__PURE__*/ _jsx(modalInfo.button.ModalComponent, {
                onSuccess: (transitionShortname, res)=>{
                    return modalInfo.resolve({
                        isSuccess: true,
                        result: {
                            res: res,
                            transitionShortname: transitionShortname
                        }
                    });
                },
                onCancel: ()=>modalInfo.resolve({
                        isSuccess: false,
                        result: {}
                    })
            })
        ]
    });
};
